"use client";

import React, { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { toast } from "components/ui/toast";
import { Check, X } from "lucide-react";
import Pagination from "components/ui/pagination";
import {
    Table,
    TableHeader,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
} from "components/ui/table";
import { getCampaignsStatsRes } from "core";
import ABCampaignResults from 'components/campaigns/ABCampaignResults';
import { ABCampaignStats, isABCampaign } from 'core/features/dashboard/abTesting';

interface CampaignStatsDetails {
    id: string;
    name: string;
    temp_name?: string;
    temp_id?: number;
    c_grp?: number;
    uuid?: string;
    s_date: string;
    e_date: string;
    c_status: string;
    email_used: string;
}

interface CampaignRow {
    SNo: number;
    name: string;
    cust_email: string;
    bounce: string;
    delivery: string;
    opened: string;
    reject: string;
    sent: string;
    spam: string;
}

interface CampaignStatsProps {
    campaignStatsDetails: CampaignStatsDetails;
}

// ===== Helpers =====
function itemsWithIndex(array: any[], currentPage = 0, pageSize = 10) {
    const offset = currentPage * pageSize;
    return array.map((item, i) => ({ ...item, SNo: offset + i + 1 }));
}
function objectItemIndex(data: any[], currentPage = 0, pageSize = 10) {
    const arr: any[] = [];
    for (let i in data) {
        data[i]["index"] = i;
        arr.push(data[i]);
    }
    return itemsWithIndex(arr, currentPage, pageSize);
}

// ===== Main Component =====
export default function CampaignStats({ campaignStatsDetails }: CampaignStatsProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [abStats, setABStats] = useState<{ id: string; data: ABCampaignStats } | null>(null);
    const requestId = useRef(0);
    const [rows, setRows] = useState<CampaignRow[]>([]);
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const [metrics, setMetrics] = useState({
        opened: 0,
        sent: 0,
        delivery: 0,
        bounce: 0,
        spam: 0,
        reject: 0,
    });

    const getCampaignStats = useCallback(async () => {
        const request = ++requestId.current;
        try {
            setIsLoading(true);
            const res: any = await getCampaignsStatsRes(campaignStatsDetails.id, page - 1);
            if (request !== requestId.current) return;
            setABStats({ id: campaignStatsDetails.id, data: res });

            setTotalRecords(res.total_customers_mapped || 0);
            setMetrics({
                opened: res.total_opened_rate || 0,
                sent: res.total_sent_rate || 0,
                delivery: res.total_delivery_rate || 0,
                bounce: res.total_bounce_rate || 0,
                spam: res.total_spam_rate || 0,
                reject: res.total_reject_rate || 0,
            });

            const base = Array.isArray(res.campaign_stats) ? res.campaign_stats : [];

            const indexed = objectItemIndex(base, page - 1, 10);


            const mapped: CampaignRow[] = indexed.map((el: any) => {
                const cd = el.customer_details ?? {};
                const first = cd.first_name ?? "";
                const last = cd.last_name ?? "";
                return {
                    SNo: el.SNo,
                    name: !first && !last ? "—" : `${first} ${last}`.trim(),
                    cust_email: cd["cust_email "] ?? cd.cust_email ?? "—",
                    bounce: el.bounce ?? "no",
                    delivery: el.delivery ?? "no",
                    opened: el.opened ?? "no",
                    reject: el.reject ?? "no",
                    sent: el.sent ?? "no",
                    spam: el.spam ?? "no",
                };
            });

            setRows(mapped);
        } catch (err) {
            if (request !== requestId.current) return;
            console.error(err);
            toast.error("Something went wrong!");
        } finally {
            if (request === requestId.current) setIsLoading(false);
        }
    }, [campaignStatsDetails.id, page]);

    useEffect(() => {
        getCampaignStats();
        return () => { requestId.current += 1; };
    }, [getCampaignStats]);

    if (abStats?.id === campaignStatsDetails.id && isABCampaign(abStats.data)) {
        return <div className="px-6 py-4"><ABCampaignResults key={campaignStatsDetails.id} campaignId={campaignStatsDetails.id} initialStats={abStats.data} /></div>;
    }

    const columns = ["S No.", "Name", "Email", "Bounce", "Delivery", "Opened", "Reject", "Sent", "Spam"];

    return (
        <div className="px-6 py-4 space-y-6 fade-in">
            {/* Header Info */}
            <Card className="p-4 bg-surface dark:bg-dark-800 shadow-sm border border-line dark:border-dark-700 rounded-xl">
                <h3 className="text-[15px] font-bold text-content dark:text-white mb-3">
                    Campaign Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    <InfoRow label="Name" value={campaignStatsDetails.name} />
                    {campaignStatsDetails.temp_name && (
                        <InfoRow label="Template" value={campaignStatsDetails.temp_name} />
                    )}
                    <InfoRow label="Start Date" value={campaignStatsDetails.s_date} />
                    <InfoRow label="End Date" value={campaignStatsDetails.e_date} />
                    <InfoRow label="Status" value={campaignStatsDetails.c_status} />
                    {campaignStatsDetails.c_grp && (
                        <InfoRow label="Segment/Group ID" value={campaignStatsDetails.c_grp.toString()} />
                    )}
                    <InfoRow label="Identity" value={campaignStatsDetails.email_used} />
                    {campaignStatsDetails.uuid && (
                        <InfoRow label="Campaign ID" value={campaignStatsDetails.uuid} />
                    )}
                </div>
            </Card>

            {/* Metrics Summary */}
            <Card className="p-4 bg-surface dark:bg-dark-800 border border-line dark:border-dark-700 rounded-xl">
                <h6 className="text-[14px] font-bold text-content dark:text-white mb-4">
                    Campaign Performance
                </h6>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-8 text-sm">
                    <Metric label="Bounce Rate" value={metrics.bounce} />
                    <Metric label="Spam Rate" value={metrics.spam} />
                    <Metric label="Delivery Rate" value={metrics.delivery} />
                    <Metric label="Open Rate" value={metrics.opened} />
                    <Metric label="Rejection Rate" value={metrics.reject} />
                    <Metric label="Sent Rate" value={metrics.sent} />
                </div>
            </Card>

            {/* Table */}
            <Card className="overflow-hidden border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 rounded-xl shadow-sm">
                {isLoading ? (
                    <div className="p-8 flex justify-center">
                        <Skeleton className="h-[200px] w-full rounded-lg" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table className="table-fixed w-full">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[70px]">S No.</TableHead>
                                    <TableHead className="min-w-[140px]">Name</TableHead>
                                    <TableHead className="min-w-[240px]">Email</TableHead>
                                    <TableHead className="w-[90px] text-center">Bounce</TableHead>
                                    <TableHead className="w-[90px] text-center">Delivery</TableHead>
                                    <TableHead className="w-[90px] text-center">Opened</TableHead>
                                    <TableHead className="w-[90px] text-center">Reject</TableHead>
                                    <TableHead className="w-[90px] text-center">Sent</TableHead>
                                    <TableHead className="w-[90px] text-center">Spam</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="text-center text-content-muted dark:text-gray-400 py-8">
                                            No Data Found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((r) => (
                                        <TableRow key={r.SNo}>
                                            <TableCell className="font-medium">{r.SNo}</TableCell>
                                            <TableCell>{r.name}</TableCell>
                                            <TableCell className="truncate max-w-[220px]">{r.cust_email}</TableCell>
                                            <IconCell value={r.bounce} />
                                            <IconCell value={r.delivery} />
                                            <IconCell value={r.opened} />
                                            <IconCell value={r.reject} />
                                            <IconCell value={r.sent} />
                                            <IconCell value={r.spam} />
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}

                <Pagination
                    totalEntries={totalRecords}
                    entriesPerPage={10}
                    currentPage={page}
                    onPageChange={setPage}
                    showSummary
                />
            </Card>
        </div>
    );
}

// ===== Subcomponents =====
function InfoRow({ label, value }: { label: string; value: string | number }) {
    return (
        <div className="flex items-start gap-2">
            <span className="text-[13px] font-semibold text-content-muted dark:text-gray-400 min-w-[110px] flex-shrink-0">{label}</span>
            <span className="text-ui font-medium text-gray-800 dark:text-gray-100 break-all leading-snug">
                {value ?? "—"}
            </span>
        </div>
    );
}

function Metric({ label, value }: { label: string; value: number }) {
    return (
        <div className="flex items-center justify-between py-1.5 border-b border-line-subtle dark:border-dark-700 last:border-0">
            <span className="text-[13px] font-medium text-content-secondary dark:text-gray-400">{label}</span>
            <span className="text-[14px] font-bold text-blue-600 dark:text-blue-400">{Math.round(value)}%</span>
        </div>
    );
}

function IconCell({ value }: { value: string }) {
    const isYes = value === "yes";
    return (
        <TableCell className="text-center">
            {isYes ? (
                <Check className="text-green-500 dark:text-green-400 inline-block h-5 w-5" />
            ) : (
                <X className="text-red-500 dark:text-red-400 inline-block h-5 w-5" />
            )}
        </TableCell>
    );
}
